import React, { useEffect, useRef, useState } from 'react';
import { defaultLayoutIcons, DefaultVideoLayout } from '@vidstack/react/player/layouts/default';
import { MediaPlayer, MediaProvider, Poster, useMediaState, useMediaRemote, Track } from '@vidstack/react';
import { SwiperSlide } from 'swiper/react';

import { SimpleImage } from '$partials/media';
import { IconPlay } from '$partials/icons';
import { Carousel, CarouselWrapper } from '$partials/base/carousel';
import { useTrackVisibility } from 'react-intersection-observer-hook';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */
const getTotalTime = (timeStamp) => {
    const time = timeStamp.split(':')?.reverse();
    const hours = time[2] ? parseInt(time[2]) * 60 * 60 : 0;
    const mins = time[1] ? parseInt(time[1]) * 60 : 0;
    const seconds = parseInt(time[0] || 0);

    return hours + mins + seconds;
};

const Seek = ({
    seek
}) => {
    const [timeStamp, setTimeStamp] = useState(-1);
    const remote = useMediaRemote();

    useEffect(() => {
        setTimeStamp(seek);
    }, [seek]);

    useEffect(() => {
        if (timeStamp !== -1) {
            remote.seek(seek);
        }
    }, [timeStamp]);

    return (
        <></>
    );
};

const Chapter = ({
    key,
    chapter,
    isPlaying,
    playerRef,
    start,
    setTime,
    startPlay,
    end
}) => {
    const [current, setCurrent] = useState(-1);

    useEffect(() => {
        return playerRef.current?.subscribe(({ currentTime }) => {
            if (parseInt(currentTime, 10) !== current) {
                setCurrent(parseInt(currentTime, 10));
            }
        });
    }, [isPlaying]);

    return (
        <button
            className='w-full bg-surface-raised rounded-lg p-16 text-left relative'
            key={key}
            onClick={() => {
                if (!isPlaying) {
                    startPlay();
                }
                setTime(start);
                setTimeout(() => {
                    setTime(-1);
                }, 300);
            }}
            type='button'>
            <p className='type-style-sans-body-med'>
                {chapter.title}
            </p>
            {chapter.text && (
                <p className='type-style-sans-body'>
                    {chapter.text}
                </p>
            )}
            <p className='type-style-sans-body'>
                {chapter.timeStamp}
            </p>
            {isPlaying && current >= start && current < end && current >= 0 ? (
                <div className='w-full bg-border-default rounded h-2 mt-16'>
                    <div
                        className='bg-border-active rounded h-full'
                        style={{ width: `${((current - start) / end) * 100}%` }}/>
                </div>
            ) : null}
        </button>
    );
};

const CarouselItems = ({
    chapters,
    isPlaying,
    startPlay,
    setTime,
    playerRef
}) => {
    return (
        <CarouselWrapper>
            {chapters?.map((chapter, idx) => {
                if (!chapter.id) { return null; }

                const timeStart = getTotalTime(chapter?.timeStamp);
                const timeEnd = chapters[idx+1] ? getTotalTime(chapters[idx+1]?.timeStamp) : playerRef?.current?.state?.duration;
                return (
                    <SwiperSlide
                        aria-roledescription='slide'
                        className='select-none cursor-pointer flex flex-wrap'
                        key={chapter.id}
                        role='group'>
                        <Chapter
                            chapter={chapter}
                            end={timeEnd}
                            isPlaying={isPlaying}
                            key={chapter.id}
                            playerRef={playerRef}
                            setTime={setTime}
                            start={timeStart}
                            startPlay={startPlay} />
                    </SwiperSlide>
                );
            }).filter(Boolean)}
        </CarouselWrapper>
    );
};

/**
 * @type {import('$types/components/partials/modules.d.ts').PanelVideoChaptersBlock}
 */

const PanelVideoChapters = ({
    title,
    chapters,
    backgroundColor,
    video
}) => {
    const videoEmbed = video.videoEmbed;
    const playerRef = useRef(null);

    const [ref, { isVisible }] = useTrackVisibility({
        rootMargin: '10px'
    });

    const startPlay = () => {
        playerRef.current.play?.();
    };
    const isStarted = useMediaState('started', playerRef);
    const isPlaying = useMediaState('playing', playerRef);
    const [time, setTime] = useState(isStarted || videoEmbed.poster ? 0 : -1);

    useEffect(() => {
        if (videoEmbed.onPlayingStateChange) {
            videoEmbed.onPlayingStateChange(!isPlaying);
        }

    }, [isPlaying, videoEmbed.onPlayingStateChange]);

    const bgColor = `bg-surface-panel-${backgroundColor}`;
    const poster = videoEmbed.poster || videoEmbed.embed.thumbnailUrl;

    useEffect(() => {
        if (isVisible && !isStarted) {
            startPlay();
        }
    }, [isVisible]);

    return (
        <section className={`${bgColor} col-wrapper-breakout pt-24 pb-16 rounded-lg flex-grow w-full`}>
            <div className='col-padding'>
                <h2 className='type-style-sans-h1 w-full md:w-7/12'>
                    {title}
                </h2>
                <div className='mt-40 flex-col-reverse md:flex-row flex gap-16'>
                    {/* Mobile: carousel */}
                    <div className='-mx-8 sm:hidden'>
                        <Carousel
                            swiperConfig={{
                                a11y: { enabled: true },
                                slidesPerView: 1.2,
                                spaceBetween: 8,
                                grabCursor: false,
                                slidesOffsetBefore: 8,
                                slidesOffsetAfter: 8
                            }}>
                            <CarouselItems
                                chapters={chapters}
                                isPlaying={isPlaying}
                                playerRef={playerRef}
                                setTime={setTime}
                                startPlay={startPlay}/>
                        </Carousel>
                    </div>
                    {/* Tablet: carousel */}
                    <div className='hidden sm:block md:hidden -mx-8'>
                        <Carousel
                            swiperConfig={{
                                a11y: { enabled: true },
                                slidesPerView: 4,
                                spaceBetween: 8,
                                grabCursor: false,
                                slidesOffsetBefore: 8,
                                slidesOffsetAfter: 8
                            }}>
                            <CarouselItems
                                chapters={chapters}
                                isPlaying={isPlaying}
                                playerRef={playerRef}
                                setTime={setTime}
                                startPlay={startPlay}/>
                        </Carousel>
                    </div>
                    {/* Desktop */}
                    <div
                        className='hidden md:flex flex-col flex-grow gap-y-8 gap-x-8'>
                        {chapters?.map((chapter, idx) => {
                            const timeStart = getTotalTime(chapter?.timeStamp);
                            const timeEnd = chapters[idx+1] ? getTotalTime(chapters[idx+1]?.timeStamp) : playerRef?.current?.state?.duration;
                            return (
                                <Chapter
                                    chapter={chapter}
                                    end={timeEnd}
                                    isPlaying={isPlaying}
                                    key={chapter.id}
                                    playerRef={playerRef}
                                    setTime={setTime}
                                    start={timeStart}
                                    startPlay={startPlay} />
                            );
                        })}
                    </div>
                    <div
                        className='w-full md:w-[1036px] relative'
                        ref={ref}>
                        <MediaPlayer
                            crossOrigin
                            playsInline
                            aspectRatio={videoEmbed.width && videoEmbed.height ? `${videoEmbed.width}/${videoEmbed.height}` : '16/9'}
                            className='rounded-lg overflow-hidden'
                            controls={true}
                            load='play'
                            muted={true}
                            onFocus={() => {
                                playerRef.current.controls?.show?.();
                            }}
                            onFullscreenChange={videoEmbed.onFullscreenChange}
                            posterLoad='eager'
                            ref={playerRef}
                            src={videoEmbed.src}
                            title={title}>
                            <MediaProvider>
                                {poster && (
                                    <Poster
                                        asChild
                                        alt='Video placeholder image'
                                        className={`vds-poster ${isStarted ? 'hidden' : ''}`}>
                                        <SimpleImage src={poster} />
                                    </Poster>
                                )}
                                {!isStarted && (
                                    <div className='absolute z-50 right-8 bottom-8'>
                                        <button
                                            className='p-12 inline-flex items-center bg-button-tint-surface rounded-full'
                                            onClick={startPlay.bind(null)}
                                            type='button'>
                                            <IconPlay/>
                                        </button>
                                    </div>
                                )}
                                <Seek seek={time}/>
                            </MediaProvider>
                            {isStarted && (
                                <DefaultVideoLayout
                                    icons={defaultLayoutIcons}
                                    slots={{
                                        title: null,
                                        chapterTitle: null,
                                        airPlayButton: null,
                                        googleCastButton: null
                                    }} />
                            )}
                        </MediaPlayer>
                    </div>
                </div>
            </div>
        </section>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default PanelVideoChapters;
